import { Phone } from 'iconoir-react'
import { ReactElement } from 'react'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { OpsTrendGraph, type TrendSeries } from './OpsTrendGraph'
import { SectionTitle } from './SectionTitle'
import SegmentControl from 'app/components/viz/SegmentControl'
import { StatsRow } from './StatsRow'
import type { CallFailureFilter, UseVoiceAgentOpsReturn } from './useVoiceAgentOps'
import { CALL_FAILURE_LABEL, CALL_FAILURE_TYPES } from './voiceAgentOpsMetrics'

const FAILURE_SERIES: TrendSeries[] = [
  { key: `GPT`, color: `blue`, value: (totals) => totals.gptFailedCalls },
  { key: `ElevenLabs`, color: `orange`, value: (totals) => totals.elevenLabsFailedCalls },
]

const FAILURE_FILTER_OPTIONS: { value: CallFailureFilter; label: string }[] = [
  { value: `all`, label: `All` },
  ...CALL_FAILURE_TYPES.map((type) => ({ value: type, label: CALL_FAILURE_LABEL[type] })),
]

type CallFailuresSectionProps = Pick<
  UseVoiceAgentOpsReturn,
  | `isLoading`
  | `summary`
  | `daily`
  | `fromDate`
  | `toDate`
  | `callFailureStats`
  | `visibleCallFailures`
  | `callFailureListColumns`
  | `failureFilter`
  | `setFailureFilter`
  | `failureCounts`
  | `callFailureListRef`
> & { highlightedCallId?: string }

/**
 * ElevenLabs & GPT failures (plan D36, D40): a card per failure type, failures over time,
 * and the failed calls filterable by type, each opening AI Call Details.
 */
export function CallFailuresSection({
  isLoading,
  summary,
  daily,
  fromDate,
  toDate,
  callFailureStats,
  visibleCallFailures,
  callFailureListColumns,
  failureFilter,
  setFailureFilter,
  failureCounts,
  callFailureListRef,
  highlightedCallId,
}: CallFailuresSectionProps): ReactElement {
  return (
    <div className="mb-8">
      <div className="mb-1">
        <SectionTitle
          title="ElevenLabs & GPT Failures"
          badge={`${summary.gptFailedCalls.toLocaleString(`en-US`)} GPT · ${summary.elevenLabsFailedCalls.toLocaleString(`en-US`)} ElevenLabs`}
        />
      </div>
      <p className="mb-3 text-body-sm text-subtle">
        GPT: the live-conversation LLM failed or was too slow. ElevenLabs: the connection dropped, the call hit its
        maximum duration, ElevenLabs reported an error, or it judged the call unsuccessful.
      </p>
      <StatsRow stats={callFailureStats} isLoading={isLoading} skeletonCount={6} />

      <div className="mt-6">
        <OpsTrendGraph
          title="Failures Over Time"
          badge={`${(summary.gptFailedCalls + summary.elevenLabsFailedCalls).toLocaleString(`en-US`)} Failures`}
          series={FAILURE_SERIES}
          daily={daily}
          fromDate={fromDate}
          toDate={toDate}
          isLoading={isLoading}
        />
      </div>

      <div ref={callFailureListRef} className="scroll-mt-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <SectionTitle title="Failed Calls" badge={`${failureCounts.all} Calls`} />
          {/* Scrolls on its own on a phone rather than pushing the page sideways. */}
          <div className="max-w-full overflow-x-auto">
            <SegmentControl
              label="Failure type"
              buttons={FAILURE_FILTER_OPTIONS.map((option) => ({
                label: `${option.label} ${failureCounts[option.value]}`,
                onClick: () => setFailureFilter(option.value),
                isActive: failureFilter === option.value,
              }))}
            />
          </div>
        </div>
        <SimpleTable
          activeSubpage="voice-agent-ops-call-failures"
          data={visibleCallFailures}
          columns={callFailureListColumns}
          enableDeepLinking={false}
          hideFooter
          meta={{ getRowStyle: (row) => (row?.id === highlightedCallId ? `table-selected-row` : ``) }}
          emptyState={
            <TableEmptyState
              icon={<Phone className="h-6 w-6" strokeWidth={1.5} />}
              title="No ElevenLabs or GPT failures"
              description="Calls with an LLM failure, a dropped connection, a max-duration cutoff, an error or an unsuccessful verdict show up here."
            />
          }
        />
      </div>
    </div>
  )
}
